import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Clock, ChefHat, Pill, HeartPulse } from 'lucide-react';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};
interface Session {
  name: string;
  duration: string;
  description: string;
  badge?: string;
  highlight?: boolean;
}
const followUps: Session[] = [{
  name: '30-Minute Follow-Up',
  duration: 'Single Session',
  description: "A focused check-in to fine-tune your plan, troubleshoot what's working, and keep momentum on track."
}, {
  name: '30-Minute Follow-Up · 3 Pack',
  duration: '3 Sessions',
  description: 'Best for anyone working toward a 2-3 month goal who wants accountability and consistent course-correction.'
}, {
  name: '30-Minute Follow-Up · 5 Pack',
  duration: '5 Sessions',
  description: 'Highly recommended if you have 20+ pounds to lose or are navigating a longer GLP-1 protocol.',
  badge: 'Popular',
  highlight: true
}, {
  name: '30-Minute Follow-Up · 10 Pack',
  duration: '10 Sessions',
  description: 'For sustained transformation — keeps you focused, motivated, and progressing toward long-term goals.'
}];
const classes = [{
  icon: ChefHat,
  title: 'Cooking Classes',
  description: "Hands-on, virtual or in-person sessions focused on high-protein, GLP-1-friendly meals you'll actually want to eat. Learn techniques, batch-prep strategies, and flavor-building principles.",
  format: 'Group or Private',
  duration: '60–90 min'
}, {
  icon: Pill,
  title: 'Supplement Education',
  description: "A deep-dive class on building a smart supplement protocol. We'll cover the essentials, what's actually worth your money, and how to evaluate quality and third-party testing.",
  format: 'Group or 1:1',
  duration: '60 min'
}, {
  icon: HeartPulse,
  title: 'Diabetes Education',
  description: 'Comprehensive class on managing blood sugar through nutrition, lifestyle, and medication coordination. Ideal for prediabetes, type 2 diabetes, or anyone wanting to optimize metabolic health.',
  format: 'Group or 1:1',
  duration: '75 min'
}];
interface SessionCardProps {
  title: string;
  duration: string;
  description: string;
  badge?: string;
  highlight?: boolean;
  variant?: 'light' | 'dark';
}
function SessionCard({
  title,
  duration,
  description,
  badge,
  highlight,
  variant = 'light'
}: SessionCardProps) {
  const isDark = variant === 'dark';
  return <div className={`relative flex flex-col h-full p-8 border transition-all ${highlight ? 'bg-tealslate text-white border-tealslate' : isDark ? 'bg-navyslate/30 border-darkgray hover:border-tealslate/60 text-white' : 'bg-white border-[#D8D8D0] hover:border-tealslate/60 text-[#1F1F1F]'}`} data-id="element-1802">
      {badge && <span className={`absolute -top-3 left-8 text-[10px] font-bold uppercase tracking-widest px-3 py-1 ${highlight ? 'bg-burgundy text-white' : 'bg-tealslate text-white'}`} data-id="element-1803">
          {badge}
        </span>}

      <div className="flex items-center gap-2 mb-4" data-id="element-1804">
        <Clock className={`w-4 h-4 ${highlight ? 'text-white/70' : isDark ? 'text-tealslate' : 'text-tealslate'}`} data-id="element-1805" />
        <span className={`text-[10px] font-bold uppercase tracking-widest ${highlight ? 'text-white/70' : isDark ? 'text-[#9CA3AF]' : 'text-[#7A7A70]'}`} data-id="element-1806">
          {duration}
        </span>
      </div>

      <h3 className={`text-2xl font-serif mb-4 leading-tight ${highlight ? 'text-white' : isDark ? 'text-white' : 'text-[#1F1F1F]'}`} data-id="element-1807">
        {title}
      </h3>

      <p className={`text-sm leading-relaxed mb-6 flex-1 ${highlight ? 'text-white/90' : isDark ? 'text-[#D8D8D0]' : 'text-[#3B3B3B]'}`} data-id="element-1808">
        {description}
      </p>

      <Link to="/contact" className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mt-auto self-start pb-1 border-b-2 transition-colors ${highlight ? 'text-white border-white hover:border-white/50' : 'text-tealslate border-tealslate hover:border-tealslate/50'}`} data-id="element-1809">
        Inquire to Book <ArrowRight className="w-3 h-3" data-id="element-1810" />
      </Link>
    </div>;
}
export function Services() {
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-1811">
      {/* HERO */}
      <section className="bg-bodybg pt-32 pb-20 px-6 border-b border-darkgray" data-id="element-1812">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1813">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="flex flex-col items-center" data-id="element-1814">
            <motion.p variants={fadeUpVariants} className="text-[10px] md:text-xs font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-6" data-id="element-1815">
              1:1 Coaching · Classes · Education
            </motion.p>
            <motion.h1 variants={fadeUpVariants} className="text-4xl md:text-6xl font-serif text-white leading-tight mb-8" data-id="element-1816">
              Services tailored to where you are.
            </motion.h1>
            <motion.div variants={fadeUpVariants} className="w-16 h-0.5 bg-tealslate mb-8" data-id="element-1817" />
            <motion.p variants={fadeUpVariants} className="text-base md:text-lg text-[#D8D8D0] max-w-2xl leading-relaxed" data-id="element-1818">
              Whether you're starting GLP-1 therapy, preserving muscle through a
              transition, or building lifelong metabolic health — there's a path
              here for you.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FEATURED PACKAGE — I Am Serious */}
      <section className="bg-[#F5F5F0] py-20 md:py-28 px-6" data-id="element-1819">
        <div className="max-w-7xl mx-auto" data-id="element-1820">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-1821">
            {/* Left: Package overview */}
            <motion.div variants={fadeUpVariants} className="lg:col-span-5" data-id="element-1822">
              <div className="flex items-center gap-2 mb-4" data-id="element-1823">
                <Star className="w-4 h-4 text-tealslate fill-tealslate" data-id="element-1824" />
                <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em]" data-id="element-1825">
                  Signature Package
                </p>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F] leading-tight mb-6" data-id="element-1826">
                I Am Serious.
              </h2>
              <div className="w-12 h-0.5 bg-tealslate mb-6" data-id="element-1827" />
              <p className="text-lg text-[#3B3B3B] leading-relaxed mb-6" data-id="element-1828">
                Built for the person ready to genuinely commit. Three structured
                sessions designed to give you a comprehensive plan, ongoing
                accountability, and the kind of momentum that creates real
                change.
              </p>
              <p className="text-sm text-[#7A7A70] italic mb-8" data-id="element-1829">
                Best for: anyone starting their journey or returning with
                serious intent. Get the foundation right from day one.
              </p>

              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors" data-id="element-1830">
                Inquire About This Package <ArrowRight className="w-4 h-4" data-id="element-1831" />
              </Link>
            </motion.div>

            {/* Right: 3 sessions */}
            <motion.div variants={fadeUpVariants} className="lg:col-span-7 space-y-4" data-id="element-1832">
              {[{
              num: '01',
              duration: '1h 20min',
              title: 'Initial Nutrition Assessment',
              desc: "A deep-dive holistic session — your story, your goals, your medical context. You'll leave with a fully comprehensive action plan tailored to your lifestyle."
            }, {
              num: '02',
              duration: '55 min',
              title: 'Post-Assessment Follow-Up',
              desc: "Booked 7–10 days after your initial session. We review your tracker, identify your most successful patterns, and lock in what's working."
            }, {
              num: '03',
              duration: '30 min',
              title: 'Follow-Up Session',
              desc: 'A focused check-in to fine-tune the plan, troubleshoot challenges, and ensure continued progress.'
            }].map((session, i) => <div key={i} className="bg-white border border-[#D8D8D0] p-6 flex gap-6 items-start" data-id="element-1833">
                  <span className="text-3xl font-serif text-tealslate shrink-0" data-id="element-1834">
                    {session.num}
                  </span>
                  <div className="flex-1" data-id="element-1835">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2" data-id="element-1836">
                      <h4 className="text-lg font-serif text-[#1F1F1F]" data-id="element-1837">
                        {session.title}
                      </h4>
                      <span className="text-[10px] font-bold text-[#7A7A70] uppercase tracking-widest" data-id="element-1838">
                        {session.duration}
                      </span>
                    </div>
                    <p className="text-sm text-[#7A7A70] leading-relaxed" data-id="element-1839">
                      {session.desc}
                    </p>
                  </div>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* INDIVIDUAL SESSIONS */}
      <section className="bg-bodybg py-20 md:py-28 px-6 border-t border-darkgray" data-id="element-1840">
        <div className="max-w-7xl mx-auto" data-id="element-1841">
          <div className="text-center mb-16" data-id="element-1842">
            <p className="text-[10px] font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-4" data-id="element-1843">
              À La Carte
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-1844">
              Individual Sessions
            </h2>
            <div className="w-12 h-0.5 bg-tealslate mx-auto mt-6" data-id="element-1845" />
            <p className="text-base text-[#9CA3AF] max-w-2xl mx-auto mt-6 leading-relaxed" data-id="element-1846">
              Already have a plan in motion? Choose individual sessions or a
              follow-up pack to fit your stage.
            </p>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-1847">
            <motion.div variants={fadeUpVariants} data-id="element-1848">
              <SessionCard title="Become a Private Client" duration="1h 15min · Initial Assessment" description="Your foundational session — we build a customized, sustainable eating pattern fit for your lifestyle. Full attention to your story, full action plan to leave with." variant="dark" data-id="element-1849" />
            </motion.div>
            <motion.div variants={fadeUpVariants} data-id="element-1850">
              <SessionCard title="Post-Initial Assessment Follow-Up" duration="55 min · Booked 7–10 days after initial" description="The critical check-in. We review your tracker, identify successful patterns, and ensure you repeat them — locking in weight loss for good." variant="dark" data-id="element-1851" />
            </motion.div>
          </motion.div>

          <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-6 text-center" data-id="element-1852">
            Follow-Up Packs
          </p>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-4" data-id="element-1853">
            {followUps.map((session, i) => <motion.div key={i} variants={fadeUpVariants} data-id="element-1854">
                <SessionCard title={session.name} duration={session.duration} description={session.description} badge={session.badge} highlight={session.highlight} variant="dark" data-id="element-1855" />
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* CLASSES */}
      <section className="bg-[#F5F5F0] py-20 md:py-28 px-6" data-id="element-1856">
        <div className="max-w-7xl mx-auto" data-id="element-1857">
          <div className="text-center mb-16" data-id="element-1858">
            <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-1859">
              Classes & Workshops
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F]" data-id="element-1860">
              One-off learning experiences.
            </h2>
            <div className="w-12 h-0.5 bg-tealslate mx-auto mt-6" data-id="element-1861" />
            <p className="text-base text-[#7A7A70] max-w-2xl mx-auto mt-6 leading-relaxed" data-id="element-1862">
              Group or private classes designed to build practical skills you'll
              use for life. Available virtually or in-person.
            </p>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1863">
            {classes.map((cls, i) => <motion.div key={i} variants={fadeUpVariants} className="bg-white border border-[#D8D8D0] p-8 flex flex-col h-full hover:border-tealslate/60 transition-colors" data-id="element-1864">
                <cls.icon className="w-8 h-8 text-tealslate mb-6" data-id="element-1865" />
                <h3 className="text-2xl font-serif text-[#1F1F1F] mb-4 leading-tight" data-id="element-1866">
                  {cls.title}
                </h3>
                <p className="text-sm text-[#7A7A70] leading-relaxed mb-6 flex-1" data-id="element-1867">
                  {cls.description}
                </p>
                <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-[#7A7A70] mb-6 pb-4 border-b border-[#D8D8D0]" data-id="element-1868">
                  <span data-id="element-1869">{cls.format}</span>
                  <span data-id="element-1870">{cls.duration}</span>
                </div>
                <Link to="/contact" className="inline-flex items-center gap-2 text-xs font-bold text-tealslate uppercase tracking-wider mt-auto self-start pb-1 border-b-2 border-tealslate hover:border-tealslate/50 transition-colors" data-id="element-1871">
                  Inquire to Schedule <ArrowRight className="w-3 h-3" data-id="element-1872" />
                </Link>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-burgundy py-20 md:py-28 px-6 text-center" data-id="element-1873">
        <div className="max-w-3xl mx-auto" data-id="element-1874">
          <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-1875">
            Not Sure Where to Start?
          </p>
          <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight mb-8" data-id="element-1876">
            Let's figure it out together.
          </h2>
          <p className="text-lg text-[#D8D8D0] leading-relaxed mb-10 max-w-xl mx-auto" data-id="element-1877">
            Send a quick message and I'll help you decide which service or
            package is the right fit for your goals.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-lightteal transition-colors" data-id="element-1878">
            Get in Touch <ArrowRight className="w-4 h-4" data-id="element-1879" />
          </Link>
        </div>
      </section>
    </div>;
}